import { useCreateFormMutation } from '@/generated/graphql';
import { Modal, notification } from 'antd';
import { useRouter } from 'next/router';
import { ParsedUrlQueryInput } from 'node:querystring';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useCurrentUser } from '../helpers/utils';
import { TENANT_PRIVACY } from '../tenant/tenant-privacy';

export const useCreateForm = (
  queryAddition?: ParsedUrlQueryInput,
  return_to?: string,
): [
  (
    clinicId: string,
    formTemplateVersioningGroupId: string,
    forwardTo?: (formRevisionGroupId: string) => string,
    onCompleted?: (formRevisionGroupId: Nullable<string>) => void,
  ) => void,
  { loading: boolean },
] => {
  const { formatMessage } = useIntl();
  const { userLoading, currentUser } = useCurrentUser();

  const router = useRouter();

  const [routeTransitioning, setRouteTransitioning] = useState(false);

  const [createFormMutation, { loading: createFormLoading }] = useCreateFormMutation();

  function createForm(
    clinicId: string,
    formTemplateVersioningGroupId: string,
    forwardTo?: (formRevisionGroupId: string) => string,
    onCompleted?: (formRevisionGroupId: Nullable<string>) => void,
  ) {
    if (clinicId) {
      createFormMutation({
        variables: {
          clinicId,
          request: {
            inSequenceMode: true,
            templateVersioningGroupId: formTemplateVersioningGroupId,
          },
        },
      }).then(({ data }) => {
        const form = data?.createForm?.response?.form;
        const formRevisionGroupId = form?.revisionInfo?.revisionGroupId;

        onCompleted?.(formRevisionGroupId);

        if (formRevisionGroupId) {
          if (forwardTo) {
            const query = {
              clinicId,
              formRevisionGroupId,
              return_to: router.pathname,
              destination: forwardTo(formRevisionGroupId),
              ...queryAddition,
            };

            if (return_to) {
              query['return_to'] = return_to;
            }

            setRouteTransitioning(true);
            router
              .push({
                pathname: '/form',
                query,
              })
              .then(() => setRouteTransitioning(false));
          }
        } else {
          notification.error({
            message: formatMessage({ id: 'error.title' }),
            description: formatMessage({ id: 'error.message' }),
          });
        }
      });
    }
  }

  const handleFormCreation = (
    clinicId: string,
    formTemplateVersioningGroupId: string,
    forwardTo?: (formRevisionGroupId: string) => string,
    onCompleted?: (formRevisionGroupId: Nullable<string>) => void,
  ) => {
    const dataPrivacyConfirmed = localStorage.getItem('privacy-confirmed') === 'true';
    const loggedIn = !!currentUser?.id;

    if (!loggedIn && !dataPrivacyConfirmed) {
      // No currently logged-in user
      Modal.confirm({
        title: formatMessage({ id: 'form.data-privacy-consent-missing.title' }),
        content: formatMessage(
          {
            id: 'form.data-privacy-consent-missing.description',
          },
          {
            privacyLink: (
              // TBD: which privacy document should be accepted
              <a href={TENANT_PRIVACY} target={'_blank'} rel={'noreferrer'}>
                {formatMessage({ id: 'privacy' })}
              </a>
            ),
          },
        ),
        okText: formatMessage({ id: 'form.data-privacy-consent-missing.accept' }),
        onOk() {
          localStorage.setItem('privacy-confirmed', 'true');
          createForm(clinicId, formTemplateVersioningGroupId, forwardTo, onCompleted);
        },
      });
    } else {
      // we are logged in or we confirmed the data privacy -> good to go
      createForm(clinicId, formTemplateVersioningGroupId, forwardTo, onCompleted);
    }
  };

  return [
    handleFormCreation,
    {
      loading: createFormLoading || routeTransitioning || (userLoading ?? false),
    },
  ];
};
